import { useMemo, useSyncExternalStore } from 'react'
import { magasin, type Instantane } from './store.ts'
import type { Table } from '../ops/index.ts'

/**
 * `useSyncExternalStore` plutôt qu'un contexte React : le magasin vit hors
 * de React, il est mis à jour par la synchronisation et par les gestes, et
 * ce hook est exactement l'API prévue pour ça. Un contexte imposerait de
 * re-rendre tout l'arbre à chaque tick de synchro.
 */
export function useMagasin(): Instantane {
  return useSyncExternalStore(magasin.abonner, magasin.lire, magasin.lire)
}

export function useTable<T = Record<string, unknown>>(nom: Table): T[] {
  const { etat } = useMagasin()
  return [...etat[nom].values()].filter((l) => !l.deletedAt) as T[]
}

export type Lieu = { id: string; name: string; emoji: string | null; sortOrder: number; isFreezer: boolean }
export type Rayon = { id: string; name: string; emoji: string | null; sortOrder: number }
export type Produit = {
  id: string
  name: string
  search: string
  aliases: string[]
  emoji: string | null
  rayonId: string | null
  defaultLocationId: string | null
  unitHint: string | null
  keepsDays: number | null
  tier: number
}
export type Article = {
  id: string
  catalogItemId: string
  locationId: string
  state: 'en_stock' | 'bientot_fini' | 'epuise'
  stateChangedAt: string | null
  note: string | null
}
export type LigneCourses = {
  id: string
  catalogItemId: string | null
  freeText: string | null
  rayonId: string | null
  rayonSortOrder: number
  state: 'a_prendre' | 'pris'
  addedReason: string
  note: string | null
  /** Absent (et non `null`) sur une ligne synchronisée avant l'ajout de la colonne. */
  quantityText?: string | null
  tripId: string | null
}
export type Peremption = { id: string; inventoryItemId: string; date: string; label: string | null }

/**
 * L'emoji à afficher pour un produit : le sien, sinon celui de son RAYON.
 *
 * 142 produits sur 386 n'ont pas d'emoji propre — la graine n'en donne
 * qu'aux plus courants, et rien n'en attribue à ce qu'on crée à la main ou
 * par un scan. Ces lignes-là s'affichaient nues au milieu des autres, ce
 * qui se lit comme un oubli alors que c'est la normale.
 *
 * La retombée sur le rayon règle le cas sans rien stocker : l'emoji reste
 * DÉRIVÉ, donc renommer un rayon ou changer son emoji se propage tout
 * seul, et il n'y a pas de copie à maintenir. C'est aussi pour ça qu'on ne
 * recopie pas l'emoji du rayon dans le produit à la création : une copie
 * se périme, une dérivation non.
 *
 * On ne descend PAS jusqu'à un emoji par défaut générique : un point
 * médian discret dit « pas de catégorie » honnêtement, là où un 🍽 partout
 * ferait croire à une catégorie qui n'existe pas.
 */
export function emojiProduit(
  produit: { emoji?: string | null; rayonId?: string | null } | undefined,
  rayons: ReadonlyMap<string, { emoji: string | null }>,
): string | null {
  if (!produit) return null
  if (produit.emoji) return produit.emoji
  return (produit.rayonId && rayons.get(produit.rayonId)?.emoji) || null
}

/** Les rayons indexés par id — l'appoint de `emojiProduit`. */
export function useRayons(): Map<string, Rayon> {
  const { etat } = useMagasin()
  return useMemo(
    () =>
      new Map(
        ([...etat.rayons.values()] as unknown as Rayon[])
          .filter((r) => !(r as unknown as { deletedAt?: unknown }).deletedAt)
          .map((r) => [r.id, r]),
      ),
    [etat],
  )
}
